import { Controller } from 'react-hook-form';
import {
  Banner,
  Button,
  Input,
  Stack,
  Typography,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Table } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useIntl from '../../../hooks/useIntl';
import useBaseTableProps from '../../../hooks/useBaseTableProps';
import messages from './messages';
import { useApprovePaymentModal } from './hooks';
import { CircleCheckIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Collapse } from '@fortress-technology-solutions/fortress-component-library/Transitions';

const ApprovePaymentModal = ({
  voucher,
  voucherStats,
  disabled = false,
  disabledTooltip,
  onSaveApproval,
  onFinalizeApproval,
  isSaving = false,
  isFinalizing = false,
}) => {
  const intl = useIntl();
  const baseTableProps = useBaseTableProps();

  const {
    count,
    totalCount,
    open,
    handleOpen,
    handleClose,
    control,
    voucherIdRules,
    isValid,
    hasAnyVariance,
    tableRows,
    headers,
    footerRow,
    tableHeight,
    handleSaveAndClose,
    handleConfirmAndFinalize,
  } = useApprovePaymentModal({
    voucher,
    voucherStats,
    onSaveApproval,
    onFinalizeApproval,
    isSaving,
    isFinalizing,
  });

  return (
    <>
      <Tooltip
        title={
          disabled
            ? disabledTooltip ??
              intl.formatMessage(messages.approvePaymentDisabledTooltip)
            : ''
        }
      >
        <Button
          variant="primary"
          onClick={handleOpen}
          disabled={disabled}
          startIcon={<CircleCheckIcon />}
        >
          {intl.formatMessage(messages.approvePayment)}
        </Button>
      </Tooltip>

      <Modal
        open={open}
        onClose={handleClose}
        title={intl.formatMessage(messages.modalTitle)}
        ModalProps={{
          PaperProps: {
            sx: {
              width: { xs: 'calc(100% - 32px)', md: 960 },
              maxWidth: '100%',
            },
          },
        }}
        actionsProps={[
          {
            variant: 'secondary',
            submitButton: true,
            children: intl.formatMessage(messages.saveAndClose),
            disabled: !isValid || isSaving || isFinalizing,
            isSubmitting: isSaving,
            onClick: handleSaveAndClose,
          },
          {
            children: intl.formatMessage(messages.confirmAndFinalize),
            submitButton: true,
            disabled: !isValid || hasAnyVariance || isSaving || isFinalizing,
            isSubmitting: isFinalizing,
            onClick: handleConfirmAndFinalize,
          },
        ]}
      >
        <Stack spacing={2} direction="column">
          <Typography variant={'bodyMedium'} display={'block'}>
            {intl.formatMessage(messages.modalSubtitle)}
          </Typography>
          {/* Voucher ID */}
          <div>
            <Controller
              name="tracsVoucherId"
              control={control}
              rules={voucherIdRules}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  label={intl.formatMessage(messages.voucherIdLabel)}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  required={true}
                  sx={{ maxWidth: 220 }}
                />
              )}
            />
          </div>

          {/* Approval Table */}
          <Table
            {...baseTableProps}
            name="approve-payment-table"
            count={count}
            totalCount={totalCount}
            headers={headers}
            rows={tableRows}
            footerRow={footerRow}
            height={tableHeight}
            isLoading={false}
            showSettings={false}
          />

          {/* Confirmation banner — visible only when form is valid */}
          <Collapse in={isValid && hasAnyVariance}>
            <Banner
              status="error"
              message={intl.formatMessage(messages.varianceErrorBanner)}
            />
          </Collapse>

          <Collapse in={isValid && !hasAnyVariance}>
            <Banner
              status="success"
              message={intl.formatMessage(messages.confirmationBanner)}
            />
          </Collapse>
        </Stack>
      </Modal>
    </>
  );
};

export default ApprovePaymentModal;
